import { action, observable, reaction } from 'mobx';
import Alert from 'react-s-alert';
import i18next from 'i18next';
import { omitBy } from 'lodash';

import { AppManager, Input } from 'core';

import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';

import { Profile } from 'generated/typescript';
import { analytics } from 'core/analytics';
import { SetupProfileModal } from './appModel';
import { removeAtSymbol, setupProfileForm } from './config';
import { ConfirmProfileMutation } from '../gql';

const MAX_INDEX = 2;

export class SetupProfileAppManager extends AppManager {
  static registryName = 'SetupProfileAppManager';

  totalStep: number;

  setupProfileStore: FormStore;

  setupProfileModal: SetupProfileModal;

  @observable currentStep: number = 0;

  constructor(parent: AppManager, init = {}, options = {}) {
    super(parent, init, options);

    this.setupProfileModal = new SetupProfileModal();
    this.SetUsernameFromEmail();
  }

  @action SetUsernameFromEmail = () => {
    if (!this.setupProfileStore) return;
    const disposer = reaction(
      () => this.setupProfileStore.target.email,
      email => {
        if (!email) return;
        const separatorIndex = email.indexOf('@');
        if (separatorIndex === -1) return;
        this.setupProfileStore.target.username = `@${email.substring(
          0,
          separatorIndex,
        )}`;
      },
    );
    this.disposers.push(disposer);
  };

  @action onError = () => {
    this.setupProfileStore.SetPosting(false);

    Alert.error(
      i18next.t(
        'Hmm... We failed to submit your Application. Please try again.',
      ),
    );
  };

  @action submitProfile = async (tagsStore: Record<string, string[]>) => {
    this.setupProfileStore.SetPosting(true);

    const { target } = this.setupProfileStore;
    const {
      company,
      company_size,
      job_title,
      phone,
      email: targetEmail,
      username: targetUsername,
      fullName: targetFullName,
    } = target;

    const {
      bio = '',
      fullName,
      username,
      email,
    } = clientStore.profileResponse as Profile;

    const baseInfo = {
      fullName: fullName || targetFullName,
      // username cannot contain '@' symbol
      username: removeAtSymbol(username || targetUsername),
    };

    const profileRequiredInfo = email
      ? baseInfo
      : {
          email: targetEmail,
          ...baseInfo,
        };

    const emptyValues = ['', null, undefined];

    const formatMetadata = omitBy(
      {
        company,
        phone: phone === '+' ? null : phone,
        company_size,
        job_title,
        ...tagsStore,
      },
      v => emptyValues.includes(v),
    );

    const profileData = new Input({
      bio,
      ...profileRequiredInfo,
      extraMetadata: JSON.stringify(formatMetadata),
    });

    const profileMutation = new ConfirmProfileMutation(
      profileData,
      null,
      {},
      false,
    );

    profileMutation.mutate({
      onSysErr: this.onError,
      onUserErr: this.onError,
      onSuccess: (profile: { profileConfirmed: boolean }) => {
        this.setupProfileStore.SetPosting(false);
        const newProfile = {
          ...(clientStore.profileResponse || {}),
          profileConfirmed: profile.profileConfirmed,
        };

        clientStore.UpdateProfileResponse(newProfile);

        Alert.success(i18next.t('Confirm profile success'));
        analytics.sendAdwordsConversion('signUp');
        clientStore.routerStore.navigate('onboarding.choosePlan');
      },
    });
  };

  @action clickNext = () => {
    if (this.setupProfileStore.isValid && this.currentStep < MAX_INDEX) {
      this.updateStep(this.currentStep + 1);
    } else {
      this.setupProfileStore.alert();
    }
  };

  @action updateStep = (step: number) => {
    if (this.setupProfileStore.isValid) {
      this.currentStep = step;
    } else {
      this.setupProfileStore.alert();
    }
  };

  @action clickBack = () => {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
  };

  @action initProfileForm = () => {
    this.setupProfileStore = new FormStore(
      this.setupProfileModal,
      setupProfileForm(clientStore.profileResponse as Profile),
      'setupProfileForm',
      false,
    );
  };
}
